// # Filename: src/components/poker/shared/stadiumMath.js
//
// Generic "stadium" (rounded-rectangle track) geometry used to place seats around the
// felt. Desktop and mobile each have their own dimensions/constants and their own
// higher-level wrapper functions, but both walk the same stadium perimeter math, so
// only that low-level math lives here.

export function stadiumPerimeter(straightHalfPct, radiusPct) {
  return 4 * straightHalfPct + 2 * Math.PI * radiusPct;
}

export function stadiumPointAtFraction(fraction, straightHalfPct, radiusPct) {
  const topLength = 2 * straightHalfPct;
  const capLength = Math.PI * radiusPct;
  const perimeter = stadiumPerimeter(straightHalfPct, radiusPct);
  let distance = (((fraction % 1) + 1) % 1) * perimeter;

  if (distance <= topLength) {
    return {
      xPct: -straightHalfPct + distance,
      yPct: -radiusPct,
      inwardX: 0,
      inwardY: 1,
    };
  }

  distance -= topLength;
  if (distance <= capLength) {
    const angle = -Math.PI / 2 + distance / radiusPct;
    return {
      xPct: straightHalfPct + radiusPct * Math.cos(angle),
      yPct: radiusPct * Math.sin(angle),
      inwardX: -Math.cos(angle),
      inwardY: -Math.sin(angle),
    };
  }

  distance -= capLength;
  if (distance <= topLength) {
    return {
      xPct: straightHalfPct - distance,
      yPct: radiusPct,
      inwardX: 0,
      inwardY: -1,
    };
  }

  distance -= topLength;
  const angle = Math.PI / 2 + distance / radiusPct;
  return {
    xPct: -straightHalfPct + radiusPct * Math.cos(angle),
    yPct: radiusPct * Math.sin(angle),
    inwardX: -Math.cos(angle),
    inwardY: -Math.sin(angle),
  };
}

export function stadiumHeroFraction(straightHalfPct, radiusPct) {
  const topLength = 2 * straightHalfPct;
  const capLength = Math.PI * radiusPct;
  return (topLength + capLength + straightHalfPct) / stadiumPerimeter(straightHalfPct, radiusPct);
}

export function stadiumPointAtPerpendicularOffset(fraction, straightHalfPct, radiusPct, offsetPct, maxHorizontalOffsetPct = null) {
  const point = stadiumPointAtFraction(fraction, straightHalfPct, radiusPct);
  const outwardX = -point.inwardX * offsetPct;
  const outwardY = -point.inwardY * offsetPct;
  const cappedOutwardX = maxHorizontalOffsetPct == null
    ? outwardX
    : Math.sign(outwardX) * Math.min(Math.abs(outwardX), maxHorizontalOffsetPct);
  return {
    ...point,
    xPct: point.xPct + cappedOutwardX,
    yPct: point.yPct + outwardY,
  };
}

// Fraction (0-1) around the stadium perimeter for the Nth opponent seat, given the
// hero's own fraction (which differs between desktop's wider stadium and mobile's
// narrower one) and how many opponents are seated.
export function opponentSeatFraction(index, opponentCount, heroFraction) {
  const safeCount = Math.max(1, Math.min(8, Number(opponentCount) || 1));
  const totalSeats = safeCount + 1;
  return (heroFraction + (index + 1) / totalSeats) % 1;
}
